import React from "react";
import { Box, Grid, IconButton, Stack } from "@mui/material";
import styled from "styled-components";
import CancelIcon from "@mui/icons-material/Cancel";
import NoDataPlaceholder from "src/components/no-data-placeholder";
import BadgeAchieventProfile from "src/components/badge-achievent-profile";
import { format, subHours } from "date-fns";
import calculateLastWeekDifference from "src/scenes/summary/summary-calculate-last-week-diff";

const SectionTitle = styled.h2`
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    padding-left: 12px;
    margin: 0;
`;

const SummaryBox = styled(Box)`
    border: 2px solid #4d4c4c;
    border-radius: 14px;
`;

const MainTitle = styled.p`
    font-size: 13px;
    margin: 0;
    margin-bottom: 3px;
    line-height: 1;
`;

const MainValue = styled.p`
    font-size: 60px;
    margin: 0;
    line-height: 1;
`;

const SubTitle = styled.p`
    font-size: 11px;
    font-weight: 200;
    margin: 0;
    line-height: 1;
    margin-bottom: 26px;
`;

const MissionsStack = styled(Stack)`
    border: 1px solid #414141;
    border-radius: 8px;
    padding: 12px;
    margin: 8px;
    width: 257px;
`;

const StartIcon = styled.img`
    width: 17px;
    margin-right: 12px;
`;

const PersonalSummary = ({ setIsPersonalSummaryVisible, summaryData }) => {
    if (!summaryData || (summaryData && summaryData.length === 0)) return null;
    const isProd = process.env.ENVIRONMENT_NAME === "prod";

    const setAssetLink = () => {
        if (!isProd) return "https://assets-sit.adadot.com/missions";
        return "https://assets.adadot.com/missions";
    };
    const assetLink = setAssetLink();

    const whatType = (metric) => {
        if (metric.type === "number") return null;
        if (metric.type === "percentile") return "%";
        if (metric.type === "number") return "hrs";
        return null;
    };

    return (
        <SummaryBox>
            <Stack direction="row" justifyContent="space-between" alignItems="center" margin={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <SectionTitle>Weekly Summary</SectionTitle>
                    <MainTitle>
                        for {format(new Date(summaryData.fromDate), "d MMM y")} -{" "}
                        {format(subHours(new Date(summaryData.toDate), 2), "d MMM y")}
                    </MainTitle>
                </Stack>
                <IconButton
                    aria-label="summary settings"
                    onClick={() => {
                        setIsPersonalSummaryVisible(false);
                        localStorage.setItem("readSummary", summaryData.createdAt);
                    }}
                >
                    <CancelIcon />
                </IconButton>
            </Stack>
            <Grid container mt={2} style={{ padding: "26px" }}>
                <Grid item md={8}>
                    <Grid container spacing={2}>
                        {summaryData &&
                            summaryData.stats.map((metric) => {
                                return (
                                    <Grid item md={4} key={metric.id}>
                                        <MainValue>
                                            {metric.value}
                                            {whatType(metric)}
                                        </MainValue>
                                        <MainTitle>{metric.name}</MainTitle>
                                        {calculateLastWeekDifference(metric)}
                                    </Grid>
                                );
                            })}
                    </Grid>
                </Grid>
                <Grid item md={3}>
                    {summaryData &&
                    summaryData.missions.length === 0 &&
                    summaryData.achievements.length === 0 ? (
                        <NoDataPlaceholder variant="summary" />
                    ) : (
                        <Stack direction="column">
                            <Stack direction="column">
                                <MainTitle>Goals Reached</MainTitle>
                                {summaryData && summaryData.missions.length > 0 ? (
                                    <Grid container direction="row">
                                        {summaryData.missions.map((mission) => (
                                            <Grid item key={mission.missionId} direction="row">
                                                <MissionsStack direction="row">
                                                    <StartIcon
                                                        src={`${assetLink}/${mission.missionCategory}/${mission.missionId}.svg`}
                                                        alt="image"
                                                    />
                                                    <Stack direction="column">
                                                        <MainTitle>
                                                            {mission.title} {mission.target}
                                                        </MainTitle>
                                                        <SubTitle style={{ marginBottom: 0 }}>
                                                            Completed on{" "}
                                                            {format(
                                                                new Date(mission.completedOn),
                                                                "d MMM y"
                                                            )}
                                                        </SubTitle>
                                                    </Stack>
                                                </MissionsStack>
                                            </Grid>
                                        ))}
                                    </Grid>
                                ) : (
                                    <NoDataPlaceholder variant="summary-missions" />
                                )}
                            </Stack>
                            <Stack direction="column">
                                <MainTitle>Latest Achievements</MainTitle>
                                {summaryData && summaryData.achievements.length > 0 ? (
                                    <Grid container direction="row">
                                        {summaryData.achievements.map((achievement) => (
                                            <BadgeAchieventProfile achievement={achievement} />
                                        ))}
                                    </Grid>
                                ) : (
                                    <NoDataPlaceholder variant="summary-achievements" />
                                )}
                            </Stack>
                        </Stack>
                    )}
                </Grid>
            </Grid>
        </SummaryBox>
    );
};

export default PersonalSummary;
